import axiosServer from "modules/axiosServer";
import {
  donationUrls,
  updatePaymentMethodUrl,
  deletePaymentProfileUrl,
  createStripeSetupIntentUrl,
  createStripePaymentIntentUrl,
  fetchStripeTerminalsUrl,
  fetchStripeTerminalConfigUrl,
} from "modules/urls";
import { setCookie, getCookie } from "modules/cookie";
import { getLandingUrlPayload, logLandingUrlsForDonation } from "modules/landingUrl";
import { sendGtmPixel } from "modules/gtm";
import { paymentSchema, transformErrorIntoObject } from "modules/validation";
import { checkRecaptchaFromError, calculateProcessingFee, reCaptchaReady, formatCampaignMessage } from "modules/utils";
import siteConfig from "src/sw/site.config";
import { setRecaptchaMode, setRecaptchaV2TokenValidated } from "../slices/recaptchaSlice";

import _ from "lodash";

import {
  updateMailChimpWithTag,
  setCustomerFulfilled,
  setCustomerDefaultSource,
  setAccountId,
  setContactId,
  setGuestId,
} from "../slices/rootSlice";
import { setCrmAccountId, setContactData } from "../slices/accountSlice";
import { getAccessToken } from "../slices/accessSlice"
import moment from "moment-timezone";

/** Backend Stripe ACH migration: map API codes to user-facing messages (donation + payment method flows). */
const PAYMENT_API_ERROR_MESSAGES = {
  SUBSCRIPTION_PAYMENT_METHOD_REBIND_REQUIRED:
    "This payment method needs to be re-authorized before it can be used again.",
  SUBSCRIPTION_PAYMENT_METHOD_PM_REQUIRED: "Please select a valid payment method.",
  legacy_payment_method_not_found: "This payment method is no longer available. Please add it again.",
  legacy_source_resolve_failed: "We could not verify this payment method. Please add it again.",
  payment_intent_already_processed: "This payment has already been recorded. If you need help, contact support.",
  payment_intent_customer_mismatch: "This payment does not match your account. Please contact support.",
  payment_intent_not_succeeded: "Your payment could not be confirmed. Please try again or contact support.",
};

/**
 * ss-server donate endpoints (PR #129): after gateway commit, HTTP 200 with
 * { paymentAccepted: true, sfdcSync: 'complete' | 'pending' } even when SF sync is deferred.
 */
export function isDonationPaymentAccepted(response) {
  if (response == null) {
    return false;
  }
  if (typeof response === "string") {
    return response === "ok";
  }
  if (typeof response !== "object") {
    return false;
  }
  if (response.paymentAccepted === true) {
    return true;
  }
  if (response.paymentAccepted === false) {
    return false;
  }
  // TODO: once ss-server #129 is deployed everywhere, require paymentAccepted === true
  // instead of accepting any object without that field (legacy { previousDonationCount }).
  return true;
}

function buildDonationSuccessPayload(response, donationType, amount, subscription, startDate) {
  const successInfo = donationSuccessMessage(
    donationType,
    amount,
    subscription,
    startDate,
    response.previousDonationCount || 0,
  );
  if (response.sfdcSync) {
    successInfo.sfdcSync = response.sfdcSync;
  }
  if (response.salesforceTransactionId) {
    successInfo.salesforceTransactionId = response.salesforceTransactionId;
  }
  if (response.salesforceSubscriptionId) {
    successInfo.salesforceSubscriptionId = response.salesforceSubscriptionId;
  }
  return successInfo;
}

function extractPaymentErrorPayload(error) {
  const nested = _.get(error, ["response", "data", "error"]);
  if (nested !== undefined && nested !== null) return nested;
  return _.get(error, ["response", "data"], error);
}

export function normalizePaymentRequestError(errorPayload) {
  if (typeof errorPayload === "string") {
    const trimmed = errorPayload.trim();
    return { message: trimmed || "Unknown error" };
  }
  const raw =
    typeof errorPayload === "object" && errorPayload !== null
      ? errorPayload
      : { message: String(errorPayload || "") };
  const code =
    raw.code ||
    raw.errorCode ||
    (typeof raw.error === "object" && raw.error?.code) ||
    (typeof raw.error === "string" ? raw.error : undefined);
  let message =
    raw.message ||
    (typeof raw.error === "object" && raw.error?.message) ||
    "";
  if (code && PAYMENT_API_ERROR_MESSAGES[code]) {
    message = PAYMENT_API_ERROR_MESSAGES[code];
  }
  return { ...raw, code: code || raw.code, message: message || raw.message || "" };
}

/** User-facing line for success alerts, e.g. "Credit card ending in 4242 has been created." */
function formatPaymentMethodSuccessDescriptor(paymentMethod) {
  if (!paymentMethod) return null;
  const last4 =
    paymentMethod.last4 ||
    _.get(paymentMethod, ["card", "last4"]) ||
    _.get(paymentMethod, ["us_bank_account", "last4"]) ||
    "";
  const isUsBank =
    !!paymentMethod.us_bank_account ||
    paymentMethod.type === "us_bank_account" ||
    _.get(paymentMethod, ["us_bank_account", "bank_name"]);
  const label = isUsBank ? "Bank account" : "Credit card";
  const tail = last4 ? ` ending in ${last4}` : "";
  return `${label}${tail}`;
}

/** Legacy ACH/bank only — card_* and card-type src_* stay selectable (not part of ACH migration). */
export function stripePaymentMethodRequiresReauthorization(paymentProfile) {
  if (!paymentProfile) return false;
  const id = paymentProfile.id || paymentProfile.customerPaymentProfileId || "";

  if (!id || id.startsWith("pm_") || id.startsWith("card_")) {
    return false;
  }
  if (id.startsWith("ba_")) {
    return true;
  }
  if (id.startsWith("src_")) {
    const srcType = paymentProfile.type;
    if (srcType === "ach_debit" || srcType === "us_bank_account") {
      return true;
    }
    if (paymentProfile.object === "bank_account") {
      return true;
    }
    return false;
  }
  if (paymentProfile.object === "bank_account") {
    return true;
  }
  if (
    paymentProfile.object === "source" &&
    (paymentProfile.type === "ach_debit" || paymentProfile.type === "us_bank_account")
  ) {
    return true;
  }
  return false;
}

export function getPaymentProfileId(paymentProfile) {
  return paymentProfile?.id || paymentProfile?.customerPaymentProfileId || null;
}

const STRIPE_CARD_BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  american_express: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  diners_club: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
};

/** Stripe pm_* brands are lowercase; legacy card_* often title-cased — normalize for display. */
export function formatStripeCardBrandLabel(brand) {
  if (!brand) return "";
  const normalized = String(brand).trim().toLowerCase().replace(/\s+/g, "_");
  if (STRIPE_CARD_BRAND_LABELS[normalized]) {
    return STRIPE_CARD_BRAND_LABELS[normalized];
  }
  return String(brand)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function paymentProfileIsDeprecated(paymentProfile) {
  return !!(
    paymentProfile &&
    (paymentProfile.deprecated || _.get(paymentProfile, "metadata.deprecated"))
  );
}

export const setTypePayment = (type) => {
  return {
    type: "SET_TYPE_PAYMENT",
    payload: type,
  };
};

export const setBankTransfer = ({ field, val }) => {
  return {
    type: "SET_BANK_TRANSFER_PAYMENT",
    payload: { [field]: val },
  };
};

export const setLoading = (flag) => {
  return {
    type: "SET_LOADING_PAYMENT",
    payload: flag,
  };
};

export const setValidationError = (error) => {
  return {
    type: "SET_VALIDATION_ERROR_PAYMENT",
    payload: error,
  };
};

export const setUseSaved = (flag) => {
  return {
    type: "SET_USE_SAVED_PAYMENT",
    payload: flag,
  };
};

export const setConfirmedMonthly = (flag) => {
  return {
    type: "SET_CONFIRMED_MONTHLY_PAYMENT",
    payload: flag,
  };
};

export const setPaymentProfiles = (paymentProfiles) => {
  return {
    type: "SET_PAYMENT_PROFILES_PAYMENT",
    payload: paymentProfiles,
  };
};

export const enterCard = (data) => {
  return {
    type: "ENTER_CARD_PAYMENT",
    payload: data,
  };
};

export const enterBank = (data) => {
  return {
    type: "ENTER_BANK_PAYMENT",
    payload: data,
  };
};

export const enterBankStripe = (data) => {
  return {
    type: "ENTER_BANK_STRIPE_PAYMENT",
    payload: data,
  };
};

export const setBillingAddress = (address) => {
  return {
    type: "SET_BILLING_ADDRESS_PAYMENT",
    payload: address,
  };
};

export const setBillingAddressSameAsContact = (flag) => {
  return {
    type: "SET_BILLING_ADDRESS_SAME_AS_CONTACT_PAYMENT",
    payload: flag,
  };
};

export const selectPaymentProfile = (id) => {
  return {
    type: "SELECT_PAYMENT_PROFILE_PAYMENT",
    payload: id,
  };
};

export const setAjsLoaded = (flag) => {
  return {
    type: "SET_AJS_LOADED_PAYMENT",
    payload: flag,
  };
};

export const setStripeLoaded = (flag) => {
  return {
    type: "SET_STRIPE_LOADED_PAYMENT",
    payload: flag,
  };
};

export const setPlaidLoaded = (flag) => {
  return {
    type: "SET_PLAID_LOADED_PAYMENT",
    payload: flag,
  };
};

export const setAsDefaultProfile = (flag) => {
  return {
    type: "SET_AS_DEFAULT_PROFILE_PAYMENT",
    payload: flag,
  };
};

export const setRequestError = (error) => {
  return {
    type: "SET_REQUEST_ERROR_PAYMENT",
    payload: error,
  };
};

export const setDisplayValidationError = (flag) => {
  return {
    type: "DISPLAY_VALIDATION_ERROR_PAYMENT",
    payload: flag,
  };
};

export const setIncludeProcessingFee = (flag) => {
  return {
    type: "SET_INCLUDE_PROCESSING_FEE_PAYMENT",
    payload: flag,
  };
};

export const setSuccessInfo = (data) => {
  return {
    type: "DONATION_SUCCESS_PAYMENT",
    payload: data,
  };
};

export const setPaymentProfileEditMode = (flag) => {
  return {
    type: "SET_PAYMENT_PROFILE_EDIT_MODE_PAYMENT",
    payload: flag,
  };
};

export const setPaymentMethodUpdateMessage = (message) => {
  return {
    type: "SET_PAYMENT_METHOD_UPDATE_MESSAGE_PAYMENT",
    payload: message,
  };
};

export const fetchStripeTerminalConfig = (
  getAccessTokenSilently,
  { recaptchaResponseToken },
  callback
) => {
  return async (dispatch, getState) => {
    try {
      const { profileAuth0, recaptchaWhitelistKey } = getState().root;
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      if (recaptchaResponseToken) {
        headers.recaptchaResponseToken = recaptchaResponseToken;
      }
      const { data: stripeTerminalConfig } = await axiosServer.get(
        fetchStripeTerminalConfigUrl,
        { headers, params: { key: recaptchaWhitelistKey } }
      );
      dispatch({
        type: "SET_STRIPE_TERMINAL_CONFIG_PAYMENT",
        payload: stripeTerminalConfig,
      });
      if (callback) {
        callback();
      }
    } catch (error) {
      console.error(error);
    }
  };
};

export const setStripeTerminal = (terminal) => {
  return {
    type: "SET_STRIPE_TERMINAL_PAYMENT",
    payload: terminal,
  };
};

export const fetchStripeTerminals = (
  getAccessTokenSilently,
  { recaptchaResponseToken },
  callback
) => {
  return async (dispatch, getState) => {
    try {
      const { profileAuth0, recaptchaWhitelistKey } = getState().root;
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      if (recaptchaResponseToken) {
        headers.recaptchaResponseToken = recaptchaResponseToken;
      }
      const {
        data: {
          terminals: stripeTerminals,
          terminalLocations: stripeTerminalLocations,
        },
      } = await axiosServer.get(fetchStripeTerminalsUrl, {
        headers,
        params: { key: recaptchaWhitelistKey },
      });
      dispatch({
        type: "SET_STRIPE_TERMINALS_PAYMENT",
        payload: stripeTerminals,
      });
      dispatch({
        type: "SET_STRIPE_TERMINAL_LOCATIONS_PAYMENT",
        payload: stripeTerminalLocations,
      });
      if (callback) {
        callback(stripeTerminals);
      }
    } catch (error) {
      console.error("Error in fetchStripeTerminals", error);
      if (callback) {
        callback([], error);
      }
    }
  };
};

const constructDonationGtmEventData = (
  profileAuth0,
  email,
  amount,
  currentAmount,
  donationType,
  isMonthly,
  paymentDate,
  isYearly,
  paymentMonth
) => {
  // Event data
  let submitEventData = {
    event: "donate_submit",
    email: profileAuth0 ? profileAuth0.email : email,
    amount,
    type: donationType,
    payment_date: isMonthly || isYearly ? paymentDate : null,
    payment_month: isYearly ? paymentMonth : null,
  };
  let successEventData = {
    event: "donate_success",
    email: profileAuth0 ? profileAuth0.email : email,
    amount,
    type: donationType,
    payment_date: isMonthly || isYearly ? paymentDate : null,
    payment_month: isYearly ? paymentMonth : null,
  };
  let failEventData = {
    event: "donate_fail",
    email: profileAuth0 ? profileAuth0.email : email,
    amount,
    type: donationType,
    payment_date: isMonthly || isYearly ? paymentDate : null,
    payment_month: isYearly ? paymentMonth : null,
  };
  if ((isMonthly || isYearly) && currentAmount && currentAmount !== amount) {
    submitEventData.type = currentAmount > amount ? "downgrade" : "upgrade";
    submitEventData.old_amount = currentAmount;
    successEventData.event =
      currentAmount > amount ? "donate_downgrade" : "donate_upgrade";
    successEventData.old_amount = currentAmount;
    failEventData.type = currentAmount > amount ? "downgrade" : "upgrade";
    failEventData.old_amount = currentAmount;
  }
  console.log([submitEventData, successEventData, failEventData]);
  return [submitEventData, successEventData, failEventData];
};

export const makeDonation = (
  getAccessTokenSilently,
  {
    cardNumber,
    accountType,
    amount,
    processingFee,
    recaptchaResponseToken,
    cardDetails,
    paymentGateway,
    preferences,
    terminalCustomerId,
    terminalPaymentMethodId,
    terminalSetupIntentId,
    setupIntentPaymentMethodId,
    completedStripePaymentIntentId,
    flow,
    stsMobileGuestContact,
  },
  force = false
) => {
  return async (dispatch, getState) => {
    const {
      //   addressId: customerAddressId,
      //   email,
      //   name: customerName,
      inspiration,
      campaignId,
      message,
      selectedCampaignOption,
    } = getState().contact;
    const { mode: recaptchaMode } = getState().recaptcha;

    const {
      contact: {
        addressId: customerAddressId,
        email,
        firstName,
        lastName,
        phone,
        address,
      },
    } = getState().account;
    const customerName = `${firstName} ${lastName}`;

    const charityRun = getState().charityRun;
    const eventRegistration = getState().eventRegistration;
    const { donationType, paymentDate, paymentMonth } = getState().options;
    const {
      accountId,
      subscription,
      subscriptionYearly,
      utms,
      profileAuth0,
      recaptchaWhitelistKey,
    } = getState().root;

    const customerProfileId =
      terminalCustomerId || getState().root.customerProfileId;

    const customerAddress = !!terminalCustomerId
      ? null
      : { ...address, phone, firstName, lastName, email };

    const {
      type: paymentType,
      selectedPaymentProfileId,
      billingAddress,
      billingAddressSameAsContact,
      asDefaultProfile,
      gateway: paymentGatewayDefault,
      includeProcessingFee,
      loading,
      paymentProfiles,
      useSaved,
    } = getState().payment;
    console.log('billingAddressSameAsContact', billingAddressSameAsContact)
    if ((loading && !force) || (getState().payment.successInfo && !force)) return;

    if (useSaved && selectedPaymentProfileId && !terminalPaymentMethodId && !setupIntentPaymentMethodId) {
      const selectedPaymentProfile = paymentProfiles.find(
        (p) => getPaymentProfileId(p) === selectedPaymentProfileId
      );
      if (stripePaymentMethodRequiresReauthorization(selectedPaymentProfile)) {
        dispatch(setLoading(false));
        return dispatch(setRequestError({
          code: "SUBSCRIPTION_PAYMENT_METHOD_REBIND_REQUIRED",
          message: PAYMENT_API_ERROR_MESSAGES.SUBSCRIPTION_PAYMENT_METHOD_REBIND_REQUIRED,
        }));
      }
    }
    // TBD: check if the same type of sub
    let currentAmount;
    const customerPaymentProfileId =
      terminalPaymentMethodId ||
      setupIntentPaymentMethodId ||
      selectedPaymentProfileId ||
      "";

    const isMonthly = donationType === "monthly";
    const isYearly = donationType === "yearly";
    if (isMonthly) {
      currentAmount = subscription
        ? _.get(subscription, "npe03__amount__c", null)
        : null;
    } else if (isYearly) {
      currentAmount = subscriptionYearly
        ? _.get(subscriptionYearly, "npe03__amount__c", null)
        : null;
    }

    const guestId = getCookie("guest_id");
    const [submitEventData, successEventData, failEventData] =
      constructDonationGtmEventData(
        profileAuth0,
        email,
        amount,
        currentAmount,
        donationType,
        isMonthly,
        paymentDate,
        isYearly,
        paymentMonth
      );
    dispatch(setLoading(true));
    dispatch(setSuccessInfo(null));
    dispatch(setRequestError(null));
    logLandingUrlsForDonation();
    sendGtmPixel({
      ...submitEventData,
      id: accountId || guestId || "N/A",
    });
    let subscriptionId = null,
      subscriptionPaymentProfileId = null;
    const paymentGatewayToUse = paymentGateway || paymentGatewayDefault;
    if (isMonthly && paymentGatewayToUse === "Stripe") {
      subscriptionId = _.get(subscription, "stripe_subscription_id__c", null);
      subscriptionPaymentProfileId = _.get(
        subscription,
        "stripe_payment_source_id__c",
        null
      );
    } else if (isMonthly && paymentGatewayToUse === "Authorize.NET") {
      subscriptionId = _.get(
        subscription,
        "authorize_net_subscription_id__c",
        null
      );
      subscriptionPaymentProfileId = _.get(
        subscription,
        "authorize_net_payment_profile_id__c",
        null
      );
    }
    if (isYearly && paymentGatewayToUse === "Stripe") {
      subscriptionId = _.get(
        subscriptionYearly,
        "stripe_subscription_id__c",
        null
      );
      subscriptionPaymentProfileId = _.get(
        subscriptionYearly,
        "stripe_payment_source_id__c",
        null
      );
    } else if (isYearly && paymentGatewayToUse === "Authorize.NET") {
      subscriptionId = _.get(
        subscriptionYearly,
        "authorize_net_subscription_id__c",
        null
      );
      subscriptionPaymentProfileId = _.get(
        subscriptionYearly,
        "authorize_net_payment_profile_id__c",
        null
      );
    }
    try {
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      let sponsorship = null
      if (typeof eventRegistration.sponsorshipPackageIndex !== 'undefined') {
        sponsorship = eventRegistration.sponsorshipParams?.packages[eventRegistration.sponsorshipPackageIndex]?.id
      }
      const { data: response = {} } = await axiosServer.post(
        `${donationUrls[donationType]}`,
        {
          recaptchaResponseToken,
          recaptchaMode,
          profileAuth0,
          paymentGateway: paymentGatewayToUse,
          paymentType,
          accountId,
          customerProfileId,
          customerPaymentProfileId,
          terminalSetupIntentId,
          subscriptionId,
          subscriptionPaymentProfileId,
          cardNumber,
          cardDetails,
          accountType,
          amount: Number(amount),
          currentAmount,
          paymentDate,
          paymentMonth: isYearly ? paymentMonth : null,
          interval: isMonthly ? "month" : isYearly ? "year" : null,
          startDate: subscription
            ? _.get(subscription, "npe03__date_established__c", null)
            : null,
          customerName: (terminalCustomerId || (flow === 'express') || (typeof window !== 'undefined' && window.location.pathname === '/donate_express')) ? customerName : null,
          customerAddress: terminalCustomerId || (flow === 'express') || (typeof window !== 'undefined' && window.location.pathname === '/donate_express') ? null : customerAddress,
          customerAddressId,
          billingAddress: billingAddressSameAsContact
            ? customerAddress
            : (customerPaymentProfileId ? null : billingAddress),
          setDefault: asDefaultProfile,
          email,
          inspiration,
          campaignId,
          campaignMessage: formatCampaignMessage(selectedCampaignOption),
          selectedCampaignOption: selectedCampaignOption && selectedCampaignOption.optionValues
            ? {
                ...selectedCampaignOption,
                option: selectedCampaignOption.optionValues[selectedCampaignOption.option] || selectedCampaignOption.option
              }
            : selectedCampaignOption,
          message,
          utms,
          includeProcessingFee,
          feeAmount: includeProcessingFee ? processingFee : 0,
          completedStripePaymentIntentId,
          preferences,
          charityRun,
          eventRegistrations: {
            sponsorship,
            ...eventRegistration,
            checkout: {
              ...eventRegistration?.checkout || null,
              feeAmount: includeProcessingFee ? calculateProcessingFee(eventRegistration?.checkout?.totalAmount || 0, null, paymentType) : 0,
            }
          },
          ...getLandingUrlPayload(),
          ...(stsMobileGuestContact ? { stsMobileGuestContact: true } : {}),
        },
        {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {},
          params: { key: recaptchaWhitelistKey },
        }
      );
      if (!isDonationPaymentAccepted(response)) {
        dispatch(setLoading(false));
        const rejectionMessage =
          "Your payment could not be confirmed. Please contact support if you were charged.";
        sendGtmPixel({
          ...failEventData,
          message: rejectionMessage,
          id: accountId || guestId || "N/A",
        });
        dispatch(setRequestError({
          message: rejectionMessage,
        }));
        return;
      }
      if (sessionStorage.getItem('layout') === 'by_step') {
        sendGtmPixel({
          event: 'donate_by_step',
          step: 'donation_complete',
          prev_step: 'payment_details',
        })
      }
      // if (getState().root.mailChimpListMemberId && !profileAuth0) {
        // dispatch(updateMailChimpWithTag('Donation_Complete'))
        // dispatch(updateMailChimpWithTag(`Donation_Complete${campaignId ? `,Campaign_${campaignId}` : ''}`));
      // }
      dispatch(updateMailChimpWithTag(`Donation_Complete${campaignId ? `,Campaign_${campaignId}` : ''}`));
      if (response && response.id) {
        sendGtmPixel({
          event: "account_created_success",
          email: profileAuth0 ? profileAuth0.email : email,
          id: response.id,
        });
        dispatch(setAccountId(response.id));
        dispatch(setCrmAccountId(response.id));
        if (!profileAuth0) {
          if (guestId) {
            dispatch(setGuestId(guestId));
          }
          if (response.contactId) {
            dispatch(setContactId(response.contactId));
            dispatch(setContactData({ crmContactId: response.contactId }));
          }
        }
      }
      let startDate = null,
        momentToday = moment().hour(0).minute(0).second(0).millisecond(0),
        momentStartDate;
      if (isMonthly) {
        momentStartDate = moment()
          .date(paymentDate)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0);
        if (momentStartDate.month() !== momentToday.month()) {
          // exceeds the max date of current month
          momentStartDate.date(1).subtract(1, "day");
        }
        if (momentStartDate.isBefore(momentToday)) {
          momentStartDate.month(momentStartDate.month() + 1);
        }
        startDate = momentStartDate.format("MMMM Do YYYY");
      } else if (isYearly) {
        momentStartDate = moment()
          .date(paymentDate)
          .month(paymentMonth)
          .hour(0)
          .minute(0)
          .second(0)
          .millisecond(0);
        if (momentStartDate.isBefore(momentToday)) {
          momentStartDate.year(momentStartDate.year() + 1);
        }
        startDate = momentStartDate.format("MMMM Do YYYY");
      }
      sendGtmPixel({
        ...successEventData,
        id: (response && response.id) || guestId || "N/A",
        paymentAccepted: response.paymentAccepted,
        sfdcSync: response.sfdcSync,
      });
      dispatch(setLoading(false));
      dispatch(
        setSuccessInfo(
          buildDonationSuccessPayload(response, donationType, amount, subscription, startDate)
        )
      );
      if (recaptchaMode === 'v2') {
        dispatch(setRecaptchaV2TokenValidated(true))
      }
    } catch (error) {
      console.error(error);
      let errorMessage = extractPaymentErrorPayload(error);
      if (errorMessage === "") {
        // maybe a 500 error
        // if (error.response?.status === 500) {
          errorMessage = "We're experiencing technical difficulties at the moment. Please try again later or contact our support team for assistance.";
        // } 
      }
      checkRecaptchaFromError(errorMessage, setRecaptchaMode, setRecaptchaV2TokenValidated, dispatch, getState) // If recaptcha v3 fails, switch to v2 mode
      sendGtmPixel({
        ...failEventData,
        message: errorMessage,
        id: accountId || guestId || "N/A",
      });
      dispatch(setLoading(false));
      let normalized = normalizePaymentRequestError(errorMessage);
      if (paymentGatewayToUse === "Stripe" && paymentType === "bank") {
        normalized = {
          ...normalized,
          forPlaid: true,
        };
      }
      dispatch(setRequestError(normalized));
    }
  };
};

export const updatePaymentMethod = (
  getAccessTokenSilently,
  {
    paymentProfileId,
    updateData,
    cardNumber,
    accountType,
    recaptchaResponseToken,
    cardDetails,
    addingNew,
    paymentGateway,
    /** After SetupIntent add-new, detach this legacy Stripe source (card_/pm_) */
    replaceLegacyPaymentProfileId,
  },
  callback
) => {
  return async (dispatch, getState) => {
    const {
      contact: { firstName, lastName, phone, email, address, addressId },
    } = getState().account;
    const customerAddress = {
      firstName,
      lastName,
      phone,
      email,
      ...address,
    };
    const {
      accountId,
      customerProfileId,
      profileAuth0,
      customer,
      recaptchaWhitelistKey,
    } = getState().root;
    const {
      type: paymentType,
      paymentProfiles,
      selectedPaymentProfileId,
      billingAddress,
      billingAddressSameAsContact,
      asDefaultProfile,
      gateway: paymentGatewayDefault,
      loading,
    } = getState().payment;
    const {
      mode: recaptchaMode,
    } = getState().recaptcha;
    const paymentGatewayToUse = paymentGateway || paymentGatewayDefault;
    if (loading) return;
    const customerPaymentProfileId = addingNew
      ? ""
      : paymentProfileId || selectedPaymentProfileId || "";
    dispatch(setLoading(true));
    dispatch(setRequestError(null));

    try {
      if (profileAuth0) await dispatch(getAccessToken())
      const accessToken = profileAuth0 ? getState().access.token : null;
    
      const { data: response = {} } = await axiosServer.post(
        updatePaymentMethodUrl,
        {
          recaptchaResponseToken,
          recaptchaMode,
          profileAuth0,
          paymentGateway: paymentGatewayToUse,
          accountId,
          customerProfileId,
          customerPaymentProfileId,
          updateData,
          cardNumber,
          cardDetails,
          accountType,
          customerAddress,
          customerAddressId: addressId,
          billingAddress: billingAddressSameAsContact
            ? customerAddress
            : billingAddress,
          setDefault: asDefaultProfile,
          email,
        },
        {
          headers: accessToken
            ? { Authorization: `Bearer ${accessToken}` }
            : {},
          params: { key: recaptchaWhitelistKey },
        }
      );
      const { summary = null, paymentMethod: upsertedPaymentMethod } = response;
      if (upsertedPaymentMethod) {
        const nextPaymentProfiles = [
          upsertedPaymentMethod,
          ...(selectedPaymentProfileId && !addingNew
            ? paymentProfiles.filter(
                (p) =>
                  (p.id || p.customerPaymentProfileId) !==
                  selectedPaymentProfileId
              )
            : paymentProfiles
          ).filter(
            (p) =>
              (p.id || p.customerPaymentProfileId) !== upsertedPaymentMethod.id
          ),
        ];
        dispatch(setPaymentProfiles(nextPaymentProfiles));
        if (asDefaultProfile && upsertedPaymentMethod.id && customer) {
          dispatch(setCustomerFulfilled(upsertedPaymentMethod.id));
        }
        if (
          addingNew &&
          replaceLegacyPaymentProfileId &&
          replaceLegacyPaymentProfileId !== upsertedPaymentMethod.id
        ) {
          // Fresh v3 token: POST /donation/payment_method already consumed the prior token.
          let deleteRecaptchaToken = recaptchaResponseToken;
          try {
            deleteRecaptchaToken = await reCaptchaReady(
              siteConfig().services.googleRecaptcha
            );
          } catch (e) {
            console.warn(
              "[paymentActions] Fresh reCAPTCHA for legacy delete failed; retry may hit duplicate",
              e
            );
          }
          await dispatch(
            removePaymentProfile(getAccessTokenSilently, {
              customerProfileId,
              customerPaymentProfileId: replaceLegacyPaymentProfileId,
              paymentGateway: paymentGatewayToUse,
              recaptchaResponseToken: deleteRecaptchaToken,
            })
          );
        }
      }
      dispatch(setLoading(false));
      const descriptor =
        formatPaymentMethodSuccessDescriptor(upsertedPaymentMethod) ||
        summary ||
        null;
      dispatch(
        setPaymentMethodUpdateMessage({
          type: "success",
          text: descriptor
            ? `${descriptor} has been ${
                selectedPaymentProfileId && !addingNew ? "updated" : "created"
              }.`
            : null,
        })
      );
      if (callback) {
        callback();
      }
      if (recaptchaMode === 'v2') {
        dispatch(setRecaptchaV2TokenValidated(true))
      }
    } catch (error) {
      console.error('paymentActions.updatePaymentMethod ---');
      console.error(error);
      let errorMessage = extractPaymentErrorPayload(error);
      checkRecaptchaFromError(errorMessage, setRecaptchaMode, setRecaptchaV2TokenValidated, dispatch, getState) // If recaptcha v3 fails, switch to v2 mode
      dispatch(setLoading(false));
      let normalized = normalizePaymentRequestError(errorMessage);
      if (paymentGatewayToUse === "Stripe" && paymentType === "bank") {
        normalized = {
          ...normalized,
          forPlaid: true,
        };
      }
      dispatch(setRequestError(normalized));
    }
  };
};

export const removePaymentProfile = (
  getAccessTokenSilently,
  {
    customerProfileId,
    customerPaymentProfileId,
    paymentGateway,
    recaptchaResponseToken,
  },
  callback
) => {
  return async (dispatch, getState) => {
    const { paymentProfiles } = getState().payment;
    const { profileAuth0, recaptchaWhitelistKey } = getState().root;
    const { mode: recaptchaMode } = getState().recaptcha;
    try {
      dispatch(setLoading(true));
      dispatch(setRequestError(null));
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      if (recaptchaResponseToken) {
        headers.recaptchaResponseToken = recaptchaResponseToken;
        headers.recaptchaMode = recaptchaMode;
      }
      const { data: nextDefaultPaymentProfileId } = await axiosServer.delete(
        `${deletePaymentProfileUrl}/${customerProfileId}/${customerPaymentProfileId}?gateway=${paymentGateway}&key=${recaptchaWhitelistKey}`,
        { headers }
      );
      const nextPaymentProfiles = paymentProfiles
        .filter(
          (p) =>
            (p.id || p.customerPaymentProfileId) !== customerPaymentProfileId
        )
        .map((p) =>
          p.customerPaymentProfileId === nextDefaultPaymentProfileId
            ? {
                ...p,
                defaultPaymentProfile: true,
              }
            : p
        );
      if (paymentGateway === "Stripe") {
        dispatch(setCustomerDefaultSource(nextDefaultPaymentProfileId));
      }
      dispatch(setPaymentProfiles(nextPaymentProfiles));
      // if we've removed all profiles
      if (!nextPaymentProfiles.length) {
        dispatch(setUseSaved(false))
      }
      dispatch(selectPaymentProfile((nextPaymentProfiles[0] || {}).id || null));
      dispatch(setLoading(false));
      if (callback) {
        callback();
      }
      if (recaptchaMode === 'v2') {
        dispatch(setRecaptchaV2TokenValidated(true))
      }
    } catch (error) {
      console.error(error);
      const errorMessage = extractPaymentErrorPayload(error);
      checkRecaptchaFromError(errorMessage, setRecaptchaMode, setRecaptchaV2TokenValidated, dispatch, getState) // If recaptcha v3 fails, switch to v2 mode
      dispatch(setLoading(false));
      dispatch(setRequestError(normalizePaymentRequestError(errorMessage)));
    }
  };
};

export const createStripeSetupIntent = (
  getAccessTokenSilently,
  { recaptchaResponseToken, setToastLoading, subscriptionId: subscriptionIdOverride },
  callback
) => {
  return async (dispatch, getState) => {
    const {
      accountId,
      customerProfileId,
      profileAuth0,
      recaptchaWhitelistKey,
      subscription,
      subscriptionYearly,
    } = getState().root;
    const { donationType } = getState().options;
    const {
      contact: { firstName, lastName, email, phone, address },
    } = getState().account;
    const { mode: recaptchaMode } = getState().recaptcha;
    let subscriptionId = subscriptionIdOverride || null;
    if (!subscriptionId && donationType === "monthly") {
      subscriptionId = _.get(subscription, "stripe_subscription_id__c", null);
    } else if (!subscriptionId && donationType === "yearly") {
      subscriptionId = _.get(subscriptionYearly, "stripe_subscription_id__c", null);
    }
    if (subscriptionId && !String(subscriptionId).startsWith("sub_")) {
      subscriptionId = null;
    }
    const name = `${firstName} ${lastName}`;
    const customerAddress = {
      firstName,
      lastName,
      email,
      phone,
      ...address,
    };
    const { billingAddress, billingAddressSameAsContact, loading } =
      getState().payment;
    if (loading) return;
    try {
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      if (recaptchaResponseToken) {
        headers.recaptchaResponseToken = recaptchaResponseToken;
        headers.recaptchaMode = recaptchaMode;
      }
      dispatch(setLoading(true));
      dispatch(setRequestError(null));
      const { data: stripeSetupIntent } = await axiosServer.post(
        createStripeSetupIntentUrl,
        {
          customerName: name,
          email,
          customerAddress,
          billingAddress,
          billingAddressSameAsContact,
          accountId,
          customerProfileId,
          ...(subscriptionId ? { subscriptionId } : {}),
        },
        { headers, params: { key: recaptchaWhitelistKey } }
      );
      // dispatch(setLoading(false))
      if (callback) {
        callback(stripeSetupIntent);
      }
      if (recaptchaMode === 'v2') {
        dispatch(setRecaptchaV2TokenValidated(true))
      }
    } catch (error) {
      console.error(error);
      const errorMessage = extractPaymentErrorPayload(error);
      checkRecaptchaFromError(errorMessage, setRecaptchaMode, setRecaptchaV2TokenValidated, dispatch, getState) // If recaptcha v3 fails, switch to v2 mode
      dispatch(setLoading(false));
      dispatch(setRequestError(normalizePaymentRequestError(errorMessage)));
      if (setToastLoading) {
        setToastLoading({ show: false });
      }
    }
  };
};

export const createStripePaymentIntent = (
  getAccessTokenSilently,
  { recaptchaResponseToken, setToastLoading, amount, feeAmount = 0 },
  callback
) => {
  return async (dispatch, getState) => {
    const {
      accountId,
      customerProfileId,
      profileAuth0,
      recaptchaWhitelistKey,
      utms,
    } = getState().root;
    const {
      contact: { firstName, lastName, email, phone, address },
    } = getState().account;
    const {
      inspiration,
      campaignId,
      message,
      selectedCampaignOption,
    } = getState().contact;
    const { mode: recaptchaMode } = getState().recaptcha;
    const name = `${firstName} ${lastName}`;
    const customerAddress = {
      firstName,
      lastName,
      email,
      phone,
      ...address,
    };
    const { billingAddress, billingAddressSameAsContact, loading } =
      getState().payment;
    if (loading) return;
    try {
      const accessToken = profileAuth0 ? await getAccessTokenSilently() : null;
      const headers = {};
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      if (recaptchaResponseToken) {
        headers.recaptchaResponseToken = recaptchaResponseToken;
        headers.recaptchaMode = recaptchaMode;
      }
      dispatch(setLoading(true));
      dispatch(setRequestError(null));
      const { data } = await axiosServer.post(
        createStripePaymentIntentUrl,
        {
          customerName: name,
          email,
          customerAddress,
          billingAddress,
          billingAddressSameAsContact,
          accountId,
          customerProfileId,
          amount,
          feeAmount,
          currency: "usd",
          utms,
          inspiration,
          campaignId,
          message,
          campaignMessage: formatCampaignMessage(selectedCampaignOption),
          selectedCampaignOption: selectedCampaignOption && selectedCampaignOption.optionValues
            ? {
                ...selectedCampaignOption,
                option: selectedCampaignOption.optionValues[selectedCampaignOption.option] || selectedCampaignOption.option
              }
            : selectedCampaignOption,
        },
        { headers, params: { key: recaptchaWhitelistKey } }
      );
      if (callback) {
        callback(data);
      }
      if (recaptchaMode === "v2") {
        dispatch(setRecaptchaV2TokenValidated(true));
      }
    } catch (error) {
      console.error(error);
      const errorMessage = extractPaymentErrorPayload(error);
      checkRecaptchaFromError(errorMessage, setRecaptchaMode, setRecaptchaV2TokenValidated, dispatch, getState);
      dispatch(setLoading(false));
      dispatch(setRequestError(normalizePaymentRequestError(errorMessage)));
      if (setToastLoading) {
        setToastLoading({ show: false });
      }
    }
  };
};

const donationSuccessMessage = (
  donationType,
  amount,
  subscription,
  startDate = null,
  previousDonationCount = 0
) => {
  let message = "";
  if (donationType === "monthly") {
    if (subscription) {
      if (subscription.amount !== amount) {
        message = `Your MONTHLY donation has been changed from $${subscription.amount} to $${amount}.`;
      } else {
        message = `You have successfully updated your contact/billing information.`;
      }
    } else {
      message = `You have successfully set a MONTHLY donation of $${amount}.`;
    }
  } else if (donationType === "yearly") {
    if (subscription) {
      if (subscription.amount !== amount) {
        message = `Your ANNUAL donation has been changed from $${subscription.amount} to $${amount}.`;
      } else {
        message = `You have successfully updated your contact/billing information.`;
      }
    } else {
      message = `You have successfully set a ANNUAL donation of $${amount}.`;
    }
  } else {
    message = `You have made a ONE-TIME donation of $${amount}.`;
  }
  return { startDate, message, previousDonationCount };
};

export const checkPaymentValidity = (site, skip = false) => {
  return async (dispatch, getState) => {
    const {
      payment: {
        // confirmedMonthly,
        type,
        card,
        bank,
        // bankStripe,
        gateway,
        useSaved,
        paymentProfiles,
        paymentProfileEditMode,
        selectedPaymentProfileId,
        billingAddressSameAsContact,
        billingAddress: { company: billingCompany, ...billingAddress },
      },
    } = getState();

    if (skip) return;

    if (
      useSaved &&
      selectedPaymentProfileId &&
      stripePaymentMethodRequiresReauthorization(
        paymentProfiles.find((p) => getPaymentProfileId(p) === selectedPaymentProfileId)
      )
    ) {
      await dispatch({
        type: "SET_PAYMENT_VALID",
        payload: false,
      });
      await dispatch({
        type: "SET_PAYMENT_ERROR",
        payload: {
          selectedPaymentProfileId: PAYMENT_API_ERROR_MESSAGES.SUBSCRIPTION_PAYMENT_METHOD_REBIND_REQUIRED,
        },
      });
      return;
    }

    try {
      await paymentSchema.validate(
        {
          card,
          type,
          bank,
          gateway,
          useSaved: paymentProfiles.length && useSaved, // true = use saved payment method on file
          paymentProfileEditMode, // true = user is on payment_methods tab, false otherwise
          selectedPaymentProfileId, // selected payment method to edit/delete
          billingAddressSameAsContact,
          billingAddress,
        },
        { abortEarly: false }
      );

      await dispatch({
        type: "SET_PAYMENT_VALID",
        payload: true,
      });
    } catch (e) {
      await dispatch({
        type: "SET_PAYMENT_ERROR",
        payload: transformErrorIntoObject(e),
      });
    }
  };
};
